import { Fragment, useState } from 'react';
import { toast } from 'sonner';
import { gql } from '@apollo/client';
import { useFragment, useMutation } from '@apollo/client/react';
import { FileSearch } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ClientError, getApiError } from 'src/utils/errors';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog';
import { SuggestedChangeVerdictEnum, AssetContentTypeEnum } from 'src/graphql/schema';
import { SuggestedChangeReviewDocument } from 'src/graphql/actions/suggestChanges/suggestedChangeReview.mutation';
import { suggestChangesVar, writeRejectSuggestChanges } from 'src/graphql/client/cache';
import { withSuspense } from 'src/hoc';

import { refetchSuggestedChanges } from '../api/refetchSuggestedChanges';

import AssetsSuggestedChanges from './AssetsSuggestedChanges';

const SuggestedChangeFragment = gql`
  fragment SuggestedChangeFragment on SuggestChange {
    id
    payload
  }
`;

const ReviewAction = ({
  suggestedChangeId,
  verdict,
  successCallback,
}: {
  suggestedChangeId: string;
  verdict: SuggestedChangeVerdictEnum;
  successCallback: () => void;
}) => {
  const [reviewChanges, { loading: isLoading }] = useMutation(SuggestedChangeReviewDocument);

  const onActionProceed = async () => {
    try {
      const { data } = await reviewChanges({
        variables: {
          suggestedChangeId,
          verdict,
        },
      });

      if (data?.suggestedChangeReview?.success) {
        successCallback();
      } else {
        throw new ClientError(data?.suggestedChangeReview?.errors?.[0]);
      }
    } catch (e: unknown) {
      getApiError(e, toast.error);
    }
  };

  return (
    <Button
      disabled={isLoading}
      variant={verdict === 'accepted' ? 'default' : 'destructive'}
      isLoading={isLoading}
      onClick={onActionProceed}
    >
      {verdict === 'accepted' ? 'Accept and send for review' : 'Reject all changes'}
    </Button>
  );
};

type Props = {
  suggestedChangeId?: string;
};

type ISuggestedChange = {
  id: string;
  payload: {
    deleted_assets: {
      id: string;
    }[];
  };
};

const Content = withSuspense(({ suggestedChange }: { suggestedChange: ISuggestedChange }) => {
  const assetsIds = suggestedChange?.payload?.deleted_assets?.map((asset) => asset?.id) || [];

  return (
    <div className="grow overflow-y-auto space-y-4 px-4 md:px-8">
      <AssetsSuggestedChanges
        title="Unlabeled assets"
        description="The assets listed below are connected to your escrow but don’t have an assigned asset type. Please review and label each asset appropriately."
        assetsIds={assetsIds}
        contentType="unlabeled"
      />
      <AssetsSuggestedChanges
        title="Source code"
        description="The core source code, including all scripts, libraries, and dependencies required to build and run the application."
        assetsIds={assetsIds}
        contentType={AssetContentTypeEnum.SourceCode}
      />
      <AssetsSuggestedChanges
        title="Documentation"
        description="Technical and operational guides, including API references, setup instructions, system architecture, and any relevant notes required to transfer, operate, and maintain the application."
        assetsIds={assetsIds}
        contentType={AssetContentTypeEnum.Documentation}
      />
      <AssetsSuggestedChanges
        title="Data"
        description="Essential datasets, configurations, and database exports necessary for the application’s functionality."
        assetsIds={assetsIds}
        contentType={AssetContentTypeEnum.Data}
      />
    </div>
  );
});

const ReviewChangesDialog = ({ suggestedChangeId }: Props) => {
  const { refetch } = useSoftwareEscrow();
  const [isSubmitChangesDialogOpen, setIsSubmitChangesDialogOpen] = useState(false);
  const [confirmationDialog, setConfirmationDialogOpen] = useState<{
    isOpen: boolean;
    verdict: SuggestedChangeVerdictEnum | null;
  }>({
    isOpen: false,
    verdict: null,
  });
  const { data: suggestedChange } = useFragment<ISuggestedChange>({
    fragment: SuggestedChangeFragment,
    from: {
      __typename: 'SuggestChange',
      id: suggestedChangeId,
    },
  });

  if (!suggestedChangeId || !suggestedChange) return null;

  const onConfirmationDialogOpenChange = (isOpen: boolean) => {
    if (isOpen) return;

    setConfirmationDialogOpen({
      isOpen: false,
      verdict: null,
    });
    refetchSuggestedChanges();
    refetch();
  };

  const successCallback = (verdict: SuggestedChangeVerdictEnum) => () => {
    setIsSubmitChangesDialogOpen(false);
    setConfirmationDialogOpen({
      isOpen: true,
      verdict,
    });
    if (verdict === SuggestedChangeVerdictEnum.Rejected) writeRejectSuggestChanges(suggestChangesVar)('deposits');
  };

  return (
    <Fragment>
      <Dialog open={isSubmitChangesDialogOpen} onOpenChange={setIsSubmitChangesDialogOpen}>
        <DialogTrigger asChild>
          <Button size="sm">
            <FileSearch />
            Review requests
          </Button>
        </DialogTrigger>
        <DialogContent className="grid-rows-[auto_1fr_auto] sm:max-w-[976px] gap-6 max-h-screen md:max-h-[calc(100vh-80px)] p-0 pt-8 pb-4 md:pb-8">
          <DialogHeader className="shrink-0 px-4 md:px-8">
            <DialogTitle className="font-bold">Do you want to remove these assets?</DialogTitle>
            <DialogDescription>
              The depositor has suggested to remove these assets from your escrow. If you accept the change, we’ll
              delete the asset and its entire version history.
              <br />
              <br />
              If you choose to reject these changes, they will be discarded and we’ll let the requesting party know.
            </DialogDescription>
          </DialogHeader>
          <Content suggestedChange={suggestedChange} />
          <DialogFooter className="shrink-0 gap-2 md:gap-6 px-4 md:px-8">
            <ReviewAction
              suggestedChangeId={suggestedChangeId}
              verdict={SuggestedChangeVerdictEnum.Rejected}
              successCallback={successCallback(SuggestedChangeVerdictEnum.Rejected)}
            />
            <ReviewAction
              suggestedChangeId={suggestedChangeId}
              verdict={SuggestedChangeVerdictEnum.Accepted}
              successCallback={successCallback(SuggestedChangeVerdictEnum.Accepted)}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmationDialog.isOpen} onOpenChange={onConfirmationDialogOpenChange}>
        <DialogContent className="sm:max-w-[441px]">
          <DialogHeader className="gap-1.5 text-center">
            <DialogTitle>Suggested changes {confirmationDialog.verdict}!</DialogTitle>
            <DialogDescription>
              {confirmationDialog.verdict === 'accepted'
                ? 'We will notify you of the outcome of our review, and as soon as these changes have been applied.'
                : 'We will notify the suggesting party. The escrow will return to its original state. '}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button className="w-full">Back to escrow</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Fragment>
  );
};

export default ReviewChangesDialog;
